'use client';

import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { DevModeIndicator } from '@/components/dev-mode-indicator';
import { DataSourceIndicator } from '@/components/data-source-indicator';
import { DatabricksConnectionIndicator } from '@/components/databricks-connection-indicator';
import { CacheStatusIndicator } from '@/components/cache-status-indicator';
import { HelpSystemIndicator } from '@/components/help-system-indicator';
import { shouldShowDebugTools, isDevMode } from '@/lib/dev-mode';
import { isHelpSystemEnabled } from '@/lib/feature-flags';
import { useEffect, useState, useRef } from 'react';
import { apiService } from '@/lib/api-service';
import { logger } from '@/lib/logger';
import type { APIResponse } from '@/lib/api-service';

interface DatabricksHealthResponse {
  status: string;
  databricks: {
    connection_type?: string;
    use_serverless?: boolean;
    configured?: boolean;
    spark_available?: boolean;
  };
}

interface CacheHealthResponse {
  status: string;
  cache_stats: {
    enabled?: boolean;
    total_entries?: number;
    active_entries?: number;
    expired_entries?: number;
  };
}

/**
 * Client component for displaying debug indicators in the user menu
 * Consolidates all debug badges into menu items
 */
export function UserMenuDebugIndicators() {
  const [databricksConnectionType, setDatabricksConnectionType] = useState<string | null>(null);
  const [cacheEnabled, setCacheEnabled] = useState<boolean | null>(null);
  const [databricksLoading, setDatabricksLoading] = useState(true);
  const [cacheLoading, setCacheLoading] = useState(true);
  const databricksIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const cacheIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const databricksRetryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cacheRetryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const databricksRetryCountRef = useRef(0);
  const cacheRetryCountRef = useRef(0);
  const isMountedRef = useRef(true);
  const databricksAbortControllerRef = useRef<AbortController | null>(null);
  const cacheAbortControllerRef = useRef<AbortController | null>(null);

  // Fetch Databricks connection type
  // React 19 Best Practice: Use AbortController for cleanup to prevent memory leaks
  // Pattern matches debug-panel.tsx implementation
  useEffect(() => {
    isMountedRef.current = true;
    
    const shouldShow = shouldShowDebugTools();
    if (!shouldShow) {
      setDatabricksLoading(false);
      return;
    }
    
    const maxRetries = 3;
    const baseDelay = 1000;
    
    // Abort any in-flight requests from previous effect run
    if (databricksAbortControllerRef.current) {
      databricksAbortControllerRef.current.abort();
    }
    
    // Create new AbortController for this effect run
    const abortController = new AbortController();
    databricksAbortControllerRef.current = abortController;
    
    async function fetchConnectionType() {
      // Check if effect was cancelled before starting
      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }
      
      try {
        const timestamp = Date.now();
        const response = await apiService.makeRequest<APIResponse<DatabricksHealthResponse>>(
          `/health/databricks?_t=${timestamp}`,
          {
            cache: 'no-store',
            signal: abortController.signal,
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0',
            },
          }
        );
        
        // Check if effect was cancelled before setting state
        if (abortController.signal.aborted || !isMountedRef.current) {
          return;
        }
        
        const responseData = (response as APIResponse<DatabricksHealthResponse>).data;
        const databricks = responseData?.databricks;
        
        if (databricks?.connection_type) {
          const type = databricks.connection_type;
          setDatabricksConnectionType(type === 'serverless' ? 'Serverless' : 'Cluster');
          setDatabricksLoading(false);
          databricksRetryCountRef.current = 0;
          
          if (databricksIntervalRef.current === null && isMountedRef.current && !abortController.signal.aborted) {
            databricksIntervalRef.current = setInterval(fetchConnectionType, 5000);
          }
        } else {
          setDatabricksLoading(false);
        }
      } catch (error: unknown) {
        // Ignore abort errors (component unmounted or navigation started)
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        
        // Don't set state if effect was cancelled
        if (abortController.signal.aborted || !isMountedRef.current) {
          return;
        }
        
        const isConnectionError = error instanceof Error && 
          (error.message.includes('ECONNREFUSED') || 
           error.message.includes('Failed to fetch') ||
           error.message.includes('NetworkError'));
        
        // Silently handle auth errors (401/403) - don't log or retry
        const isAuthError = error instanceof Error && 
          (error.message.includes('401') || error.message.includes('403'));
        
        if (!isConnectionError && !isAuthError) {
          logger.debug('Failed to fetch Databricks connection type', { error });
        }
        
        // Only retry on connection errors, not auth errors
        if (isConnectionError && databricksRetryCountRef.current < maxRetries && isMountedRef.current && !abortController.signal.aborted) {
          databricksRetryCountRef.current++;
          const delay = baseDelay * Math.pow(2, databricksRetryCountRef.current - 1);
          
          if (databricksRetryTimeoutRef.current) {
            clearTimeout(databricksRetryTimeoutRef.current);
          }
          
          databricksRetryTimeoutRef.current = setTimeout(fetchConnectionType, delay);
          return;
        }
        
        if (isMountedRef.current && !abortController.signal.aborted) {
          setDatabricksLoading(false);
        }
      }
    }

    const initialTimeout = setTimeout(() => {
      if (isMountedRef.current && !abortController.signal.aborted) {
        fetchConnectionType();
      }
    }, baseDelay);
    
    // Cleanup function: abort in-flight requests when effect re-runs or component unmounts
    // React 19 Best Practice: Cleanup function should abort all in-flight requests
    return () => {
      isMountedRef.current = false;
      clearTimeout(initialTimeout);
      abortController.abort();
      if (databricksRetryTimeoutRef.current) {
        clearTimeout(databricksRetryTimeoutRef.current);
        databricksRetryTimeoutRef.current = null;
      }
      if (databricksIntervalRef.current) {
        clearInterval(databricksIntervalRef.current);
        databricksIntervalRef.current = null;
      }
    };
  }, []);

  // Fetch cache status
  // React 19 Best Practice: Use AbortController for cleanup to prevent memory leaks
  // Pattern matches debug-panel.tsx implementation
  useEffect(() => {
    isMountedRef.current = true;
    
    const shouldShow = shouldShowDebugTools();
    if (!shouldShow) {
      setCacheLoading(false);
      return;
    }
    
    const maxRetries = 3;
    const baseDelay = 1000;
    
    // Abort any in-flight requests from previous effect run
    if (cacheAbortControllerRef.current) {
      cacheAbortControllerRef.current.abort();
    }
    
    // Create new AbortController for this effect run
    const abortController = new AbortController();
    cacheAbortControllerRef.current = abortController;
    
    async function fetchCacheStatus() {
      // Check if effect was cancelled before starting
      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }
      
      try {
        const timestamp = Date.now();
        const response = await apiService.makeRequest<APIResponse<CacheHealthResponse>>(
          `/health/cache?_t=${timestamp}`,
          {
            cache: 'no-store',
            signal: abortController.signal,
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0',
            },
          }
        );
        
        // Check if effect was cancelled before setting state
        if (abortController.signal.aborted || !isMountedRef.current) {
          return;
        }
        
        const responseData = (response as APIResponse<CacheHealthResponse>).data;
        const cacheStats = responseData?.cache_stats;
        
        if (cacheStats?.enabled !== undefined) {
          setCacheEnabled(cacheStats.enabled);
          setCacheLoading(false);
          cacheRetryCountRef.current = 0;
          
          if (cacheIntervalRef.current === null && isMountedRef.current && !abortController.signal.aborted) {
            cacheIntervalRef.current = setInterval(fetchCacheStatus, 5000);
          }
        } else {
          setCacheLoading(false);
        }
      } catch (error: unknown) {
        // Ignore abort errors (component unmounted or navigation started)
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        
        // Don't set state if effect was cancelled
        if (abortController.signal.aborted || !isMountedRef.current) {
          return;
        }
        
        const isConnectionError = error instanceof Error && 
          (error.message.includes('ECONNREFUSED') || 
           error.message.includes('Failed to fetch') ||
           error.message.includes('NetworkError'));
        
        // Silently handle auth errors (401/403) - don't log or retry
        const isAuthError = error instanceof Error && 
          (error.message.includes('401') || error.message.includes('403'));
        
        if (!isConnectionError && !isAuthError) {
          logger.debug('Failed to fetch cache status', { error });
        }
        
        // Only retry on connection errors, not auth errors
        if (isConnectionError && cacheRetryCountRef.current < maxRetries && isMountedRef.current && !abortController.signal.aborted) {
          cacheRetryCountRef.current++;
          const delay = baseDelay * Math.pow(2, cacheRetryCountRef.current - 1);
          
          if (cacheRetryTimeoutRef.current) {
            clearTimeout(cacheRetryTimeoutRef.current);
          }
          
          cacheRetryTimeoutRef.current = setTimeout(fetchCacheStatus, delay);
          return;
        }
        
        if (isMountedRef.current && !abortController.signal.aborted) {
          setCacheLoading(false);
        }
      }
    }

    const initialTimeout = setTimeout(() => {
      if (isMountedRef.current && !abortController.signal.aborted) {
        fetchCacheStatus();
      }
    }, baseDelay);
    
    // Cleanup function: abort in-flight requests when effect re-runs or component unmounts
    // React 19 Best Practice: Cleanup function should abort all in-flight requests
    return () => {
      isMountedRef.current = false;
      clearTimeout(initialTimeout);
      abortController.abort();
      if (cacheRetryTimeoutRef.current) {
        clearTimeout(cacheRetryTimeoutRef.current);
        cacheRetryTimeoutRef.current = null;
      }
      if (cacheIntervalRef.current) {
        clearInterval(cacheIntervalRef.current);
        cacheIntervalRef.current = null;
      }
    };
  }, []);

  // Don't render anything if debug tools shouldn't be shown
  if (!shouldShowDebugTools() && !isDevMode()) {
    return null;
  }

  const hasAnyIndicator = 
    isDevMode() ||
    (shouldShowDebugTools() && (
      (!databricksLoading && databricksConnectionType) ||
      (!cacheLoading && cacheEnabled !== null) ||
      true // Help system indicator always shows if debug tools are enabled
    ));

  if (!hasAnyIndicator) {
    return null;
  }

  return (
    <>
      {/* Dev Mode Indicator */}
      {isDevMode() && (
        <DropdownMenuItem disabled className="opacity-100">
          <div className="flex items-center gap-2 w-full">
            <span>🚧</span>
            <span className="text-yellow-800 font-medium">DEV MODE - No Auth Required</span>
          </div>
        </DropdownMenuItem>
      )}

      {/* Data Source Indicator */}
      {shouldShowDebugTools() && (
        <DropdownMenuItem disabled className="opacity-100">
          <div className="flex items-center gap-2 w-full">
            <span>📊</span>
            <span className="text-purple-800 font-medium">Databricks Data</span>
          </div>
        </DropdownMenuItem>
      )}

      {/* Databricks Connection Indicator */}
      {shouldShowDebugTools() && !databricksLoading && databricksConnectionType && (
        <DropdownMenuItem disabled className="opacity-100">
          <div className="flex items-center gap-2 w-full">
            <span>{databricksConnectionType === 'Serverless' ? '☁️' : '🖥️'}</span>
            <span className={databricksConnectionType === 'Serverless' ? 'text-blue-800' : 'text-orange-800'}>{databricksConnectionType}</span>
          </div>
        </DropdownMenuItem>
      )}

      {/* Cache Status Indicator */}
      {shouldShowDebugTools() && !cacheLoading && cacheEnabled !== null && (
        <DropdownMenuItem disabled className="opacity-100">
          <div className="flex items-center gap-2 w-full">
            <span>{cacheEnabled ? '💾' : '🚫'}</span>
            <span className={cacheEnabled ? 'text-green-800' : 'text-gray-800'}>{cacheEnabled ? 'Cache On' : 'Cache Off'}</span>
          </div>
        </DropdownMenuItem>
      )}

      {/* Help System Indicator */}
      {shouldShowDebugTools() && (
        <DropdownMenuItem disabled className="opacity-100">
          <div className="flex items-center gap-2 w-full">
            <span>{isHelpSystemEnabled() ? '💡' : '🚫'}</span>
            <span className={isHelpSystemEnabled() ? 'text-blue-800' : 'text-gray-800'}>{isHelpSystemEnabled() ? 'Help On' : 'Help Off'}</span>
          </div>
        </DropdownMenuItem>
      )}
    </>
  );
}

